import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getCurrentStep,
  getInfoById,
  getOnBoardingID,
  getOnBoardingName,
  getStepNumber,
  getVars,
  isLoading,
} from '../../../../redux/onBoarding/selector';
import { InfoPageStyled, InfoSliderWrapper, InfoWrapper, LoadParamsText } from './OnBoardingInfo.style';
import InfoSlider from '../../components/InfoSlider';
import { RouteComponentProps } from 'react-router-dom';
import { IonContent, useIonViewDidEnter, useIonViewDidLeave } from '@ionic/react';
import OnBoardingHeader from '../../components/OnBoardingHeader/OnBoardingHeader';
import { Container, CustomButton } from '@mindly/ui-components';
import OnBoardingFooter from '../../components/OnBoardingFooter';
import useOnBoardingNextStep from '../../../../hooks/useOnBoardingNextStep';
import useOnBoardingCurrentStep from '../../../../hooks/useOnBoardingCurrentStep';
import { OnBoardingTypes } from '../../../../redux/onBoarding/types';
import AnalyseProgress from '../../components/AnalyseProgress';
import { FormattedMessage } from 'react-intl';
import { ImpactStyle } from '@capacitor/haptics';
import { FunnelTypeEnum, useAnalytics } from 'src/shared';
import { setSplashColor } from 'src/shared/utils/splashColor';
import { Style } from '@capacitor/status-bar';
import { globalVariables } from 'src/redux/firestoreGlobalVariables/selector';
import { useTranslation } from 'react-i18next';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';

interface OnBoardingInfoProps extends RouteComponentProps<{ boardId: string; id: string }> {}

const OnBoardingInfo: FC<OnBoardingInfoProps> = ({ match }) => {
  const { id } = match.params;

  const { i18n } = useTranslation();

  const [loadParams, setLoadParams] = useState(false);
  const [analyseItemsCount, setAnalyseItems] = useState(0);
  const [isPageView, setIsPageView] = useState(false);
  const [isEventTracked, setIsEventTracked] = useState(false);

  const currentInfo = useSelector(getInfoById(id));
  const currentStep = useSelector(getCurrentStep);
  const vars = useSelector(getVars);
  const isLoadingSelector = useSelector(isLoading);
  const onBoardingName = useSelector(getOnBoardingName);
  const stepNumber = useSelector(getStepNumber);
  const onBoardingId = useSelector(getOnBoardingID);
  const globalVars = useSelector(globalVariables);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const pageCounterRef = useRef(0);

  const nextStepAction = useOnBoardingNextStep();
  const currentStepAction = useOnBoardingCurrentStep({
    ...match.params,
    type: OnBoardingTypes.INFO,
  });
  const { trackPredefinedEvent } = useAnalytics();

  useIonViewDidEnter(() => {
    currentStepAction();

    setIsPageView(true);
    setIsEventTracked(false);

    if (currentInfo?.whiteBg) {
      setSplashColor('#fffffe');
    } else {
      setSplashColor('#177590', Style.Dark);
    }

    if (!wrapperRef.current) return;
    if (!currentInfo?.isAnalyseAnswersPage) return;

    let listContainer: HTMLUListElement | HTMLOListElement | null = wrapperRef.current?.querySelector('ol');
    if (!listContainer) {
      listContainer = wrapperRef.current?.querySelector('ul');
    }
    const liElements = listContainer?.querySelectorAll('li');
    if (!liElements?.length) return;

    setAnalyseItems(liElements.length);
    setTimeout(
      () => {
        setLoadParams(true);
      },
      liElements.length * 1.5 * 1000
    );

    liElements?.forEach((e, index) => {
      setTimeout(() => {
        e.style.opacity = '1';
      }, 1500 * index);
    });
  }, [currentStepAction, id, currentStep, wrapperRef, currentInfo]);

  useIonViewDidLeave(() => {
    setLoadParams(false);
    setIsPageView(false);
    setIsEventTracked(false);
    pageCounterRef.current = 0;
  });

  useEffect(() => {
    if (!isPageView) return;
    if (pageCounterRef.current > 0) return;
    pageCounterRef.current += 1;
  }, [isPageView, stepNumber, onBoardingName, pageCounterRef]);

  const handleNextStep = useCallback(async () => {
    await nextStepAction();
  }, [nextStepAction]);

  const handleAnalyticEvent = () => {
    if (!onBoardingId || !currentInfo) return;

    trackPredefinedEvent('FUNNEL_SCREEN_VIEWED', {
      funnel_id: onBoardingId,
      funnel_name: onBoardingName,
      funnel_type:
        onBoardingId === globalVars?.defaultOnBoardingId?.[i18n.language]
          ? FunnelTypeEnum.DEFAULT
          : FunnelTypeEnum.WEB_TO_APP,
      screen_id: id,
      screen_name: `Info screen: ${currentInfo.id}`,
      screen_order: stepNumber,
    });
    setIsEventTracked(true);
  };

  useEffect(() => {
    if (!isPageView) return;
    if (isLoadingSelector) return;
    if (!onBoardingId || !onBoardingName || !currentInfo || !stepNumber) return;
    if (isEventTracked) return;

    handleAnalyticEvent();
  }, [isPageView, isLoadingSelector, isEventTracked, onBoardingId, onBoardingName, currentInfo, stepNumber]);

  return (
    <InfoPageStyled className={`${currentInfo?.whiteBg !== true ? 'info' : ''}`}>
      {!currentInfo?.hideHeader && <OnBoardingHeader hideBackButton={currentInfo?.hideBackButton} />}
      <IonContent className={`${currentInfo?.whiteBg ? 'white' : 'blue'}`}>
        <Container>
          <InfoWrapper
            ref={wrapperRef}
            className={`${currentInfo?.whiteBg ? 'white' : ''} ${
              currentInfo?.isAnalyseAnswersPage ? 'animated-list' : ''
            }`}
            dangerouslySetInnerHTML={{
              __html:
                currentInfo?.text
                  .replace(/{{(\w*)}}/g, (m, key) => (vars.hasOwnProperty(key) ? vars[key] : m))
                  ?.replace(/{{(\w*)}}/g, (m, key) => (vars.hasOwnProperty(key) ? vars[key] : m)) ?? '',
            }}
          />
          {currentInfo?.isAnalyseAnswersPage && (
            <LoadParamsText className={`${loadParams ? 'loaded' : ''}`}>
              <FormattedMessage
                id={'on-boarding.info.wait'}
                defaultMessage={'Зачекайте, завантажуємо ще 19 параметрів'}
              />
              ...
            </LoadParamsText>
          )}
          {currentInfo?.showSlider && (
            <InfoSliderWrapper>
              <InfoSlider />
            </InfoSliderWrapper>
          )}
        </Container>
      </IonContent>
      <OnBoardingFooter isAnalyse={currentInfo?.isAnalyseAnswersPage}>
        {currentInfo?.isAnalyseAnswersPage ? (
          <AnalyseProgress onFinish={handleNextStep} itemsCount={analyseItemsCount} />
        ) : (
          <CustomButton
            type="button"
            buttonType="primary"
            onClick={async () => {
              isomorficHapticsImpact({ style: ImpactStyle.Light });
              await handleNextStep();
            }}
            disabled={isLoadingSelector}
            loading={isLoadingSelector}
          >
            {currentInfo?.buttonLabel ? (
              currentInfo.buttonLabel
            ) : (
              <FormattedMessage id={'general.btn.clearly'} defaultMessage={'Зрозуміло'} />
            )}
          </CustomButton>
        )}
      </OnBoardingFooter>
    </InfoPageStyled>
  );
};

export default React.memo(OnBoardingInfo);
